import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState } from "../../../app/store";
import { TrendCurveChartData } from "../../../interface/DesignerInterface";
import Dropdown from "../Dropdown";
import TrendCurvesGraph from "./TrendCurvesGraph";

import Skeleton from "react-loading-skeleton";
import {
  trendCurveWindows,
  TrendCurveWindowType,
  normalizeTrendCurveWindowLabel,
} from "../../../constants/constants";
import {
  formatAndSetDataForTrendCurves,
  getCachedTrendCurveChartData,
  getCachedTrendCurveChartForWindow,
  getLiveTrendCurveWindowLabel,
  persistTrendCurvePreferences,
  TREND_CURVE_LIVE_FILTER_TYPE,
} from "../../../utils/trendSidebarGraphUtils/trendCurvesUtils";
import { getActiveTrendReqId } from "../../../utils/trendSidebarGraphUtils/graphHelperUtils";
import { useNavigate } from "react-router-dom";

const TrendCurvesSidebar: React.FC = () => {
  const [curveWindowType, setCurveWindowType] = useState<TrendCurveWindowType>(
    () => getLiveTrendCurveWindowLabel()
  );
  const [trendsCurveData, setTrendsCurveData] =
    useState<TrendCurveChartData | null>(
      () =>
        getCachedTrendCurveChartData(getLiveTrendCurveWindowLabel()) ??
        getCachedTrendCurveChartForWindow(getLiveTrendCurveWindowLabel())
    );
  const [loading, setLoading] = useState(
    () =>
      !getCachedTrendCurveChartData(getLiveTrendCurveWindowLabel()) &&
      !getCachedTrendCurveChartForWindow(getLiveTrendCurveWindowLabel())
  );
  const navigate = useNavigate();

  const user = useSelector((state: RootState) => state.user.userInfo);
  const graphRevision = useSelector(
    (state: RootState) => state.trends.graphRevision
  );
  const reduxReqId = useSelector(
    (state: RootState) => state.trends.trendsData?.req_id
  );
  const trendCurveRefreshKey = useSelector(
    (state: RootState) => state.trends.trendsData?.trendCurveRefreshKey ?? 0
  );
  const reqId = reduxReqId || getActiveTrendReqId();

  const fetchTrendsCurveData = async () => {
    try {
      if (
        !getCachedTrendCurveChartData(curveWindowType) &&
        !getCachedTrendCurveChartForWindow(curveWindowType) &&
        !(trendsCurveData && trendsCurveData.datasets.length > 0)
      ) {
        setLoading(true);
      }
      await formatAndSetDataForTrendCurves(
        TREND_CURVE_LIVE_FILTER_TYPE,
        curveWindowType,
        user,
        setTrendsCurveData
      );
    } catch (error) {
      console.error("Error fetching trend curve data", error);
      if ((error as AxiosError)?.response?.status !== 400) {
        toast.error("Error fetching trend curve data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!reqId) return;
    persistTrendCurvePreferences(curveWindowType, reqId);
    fetchTrendsCurveData();
  }, [curveWindowType, reduxReqId, trendCurveRefreshKey, graphRevision]);

  const hasChartData = trendsCurveData && trendsCurveData.datasets.length > 0;

  return (
    <div className="mx-3">
      <div
        className="absolute right-2 top-1 text-right text-xs underline font-bold cursor-pointer"
        onClick={() =>
          navigate("/designer-page/trend-curves", {
            state: { curveWindowType },
          })
        }
      >
        Expand
      </div>
      <div className="relative z-20 text-sm text-secondary mt-2 mb-1">
        Curve Window
      </div>
      <div className="relative z-20">
        <Dropdown
          label={curveWindowType || "Month"}
          options={[...trendCurveWindows]}
          onSelect={option => {
            const nextWindow = normalizeTrendCurveWindowLabel(option);
            if (nextWindow !== curveWindowType) {
              setCurveWindowType(nextWindow);
              if (reqId) {
                persistTrendCurvePreferences(nextWindow, reqId);
              }
            }
          }}
        />
      </div>
      {loading ? (
        <div className="relative z-0 my-10 flex items-center justify-center">
          <Skeleton height={300} width={430} />
        </div>
      ) : hasChartData ? (
        <div className="relative z-0 mt-2">
          <TrendCurvesGraph
            trendCurveData={trendsCurveData}
            trendsCurveExpandedView={false}
            showLegend={false}
            fillContainer
          />
        </div>
      ) : (
        <div className="my-10 text-center">No Trend Curve Data</div>
      )}
    </div>
  );
};

export default TrendCurvesSidebar;
